import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { supabase } from '../utils/supabaseClient';
import { Conversation } from '../types/Conversation';
import { Message } from '../types/Message';
import { RootState } from './store';
import { Agent } from '../types/Agents';

interface ConversationsState {
  conversations: Conversation[];
  messages: { [key: string]: Message[] };
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: ConversationsState = {
  conversations: [],
  messages: {},
  status: 'idle',
  error: null,
};

export const fetchConversations = createAsyncThunk(
  'conversations/fetchConversations',
  async () => {
    const { data, error } = await supabase
      .from('conversations')
      .select(
        'id, caller_id, created_at, duration, agent_id, counts_toward_usage, messages(*)'
      )
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as Conversation[];
  }
);

export const fetchConversationsByCallerId = createAsyncThunk(
  'conversations/fetchConversationsByCallerId',
  async (callerId: string) => {
    const { data, error } = await supabase
      .from('conversations')
      .select('*')
      .eq('caller_id', callerId) // Filter by caller_id
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as Conversation[];
  }
);

export const fetchMessagesByConversationId = createAsyncThunk(
  'conversations/fetchMessagesByConversationId',
  async (conversation_id: string) => {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('conversation_id', conversation_id); // Filter by conversation_id

    if (error) throw error;
    return data as Message[]; // Ensure it returns Message[]
  }
);

export const deleteConversation = createAsyncThunk(
  'conversations/deleteConversation',
  async (conversationId: string) => {
    const { error } = await supabase
      .from('conversations')
      .delete()
      .eq('id', conversationId);

    if (error) throw error;
    return conversationId;
  }
);

// New thunk to handle agent data
export const setAgentConversations = createAsyncThunk(
  'conversations/setAgentConversations',
  async (agent: Agent) => {
    const conversations = agent.conversations || [];
    const messages: { [key: string]: Message[] } = {};

    // Extract messages from conversations
    conversations.forEach(conversation => {
      if (conversation.messages) {
        messages[conversation.id] = conversation.messages;
      }
    });

    return { conversations, messages };
  }
);

const conversationsSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    resetConversationsState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(fetchConversations.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        fetchConversations.fulfilled,
        (state, action: PayloadAction<Conversation[]>) => {
          state.status = 'succeeded';
          state.conversations = action.payload;
        }
      )
      .addCase(fetchConversations.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      })
      .addCase(
        fetchConversationsByCallerId.fulfilled,
        (state, action: PayloadAction<Conversation[]>) => {
          state.status = 'succeeded';
          state.conversations = action.payload;
        }
      )
      .addCase(deleteConversation.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        deleteConversation.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.status = 'succeeded';
          state.conversations = state.conversations.filter(
            conversation => conversation.id !== action.payload
          );
        }
      )
      .addCase(deleteConversation.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      })
      .addCase(
        setAgentConversations.fulfilled,
        (
          state,
          action: PayloadAction<{
            conversations: Conversation[];
            messages: { [key: string]: Message[] };
          }>
        ) => {
          state.status = 'succeeded';
          state.conversations = action.payload.conversations;
          state.messages = action.payload.messages;
        }
      );
  },
});

export const { resetConversationsState } = conversationsSlice.actions;

export const selectConversations = (state: RootState) =>
  state.conversations.conversations;
export const selectConversationsStatus = (state: RootState) =>
  state.conversations.status;
export const selectConversationsError = (state: RootState) =>
  state.conversations.error;
export const selectConversationsByCallerId = (state: RootState) =>
  state.conversations.conversations;
export const selectMessagesByConversationId = (
  state: RootState,
  conversationId: string
) => state.conversations.messages[conversationId] || [];

export default conversationsSlice.reducer;
